import { AlertCircle, Camera, Check, Trash2, Upload } from "lucide-react";
import React, { useEffect, useState } from "react";
import toast from "react-hot-toast";

import ConfirmDialog from "@/components/common/ConfirmDialog";
import HotToast from "@/components/common/HotToast";
import businessInfoService from "@/services/businessInfoService";
import documentsService from "@/services/documentsService";

interface RestaurantApplicationCardProps {
  isSubmitting?: boolean;
  onDocumentUploaded?: () => void;
  onSubmit?: (data: any) => void;
  showDocuments?: boolean;
}

const RestaurantApplicationCard: React.FC<RestaurantApplicationCardProps> = ({
  isSubmitting = false,
  onDocumentUploaded,
  onSubmit,
  showDocuments = true,
}) => {
  // const [confirmOpen, setConfirmOpen] = useState(false); // Removed duplicate
  const [deleting, setDeleting] = useState(false);
  const [formData, setFormData] = useState({
    address: "",
    coverPhoto: null as File | null,
    coverPhotoUrl: "",
    cuisineType: "",
    description: "",
    documents: {
      businessLicense: null as File | null,
      healthPermit: null as File | null,
      menuSample: null as File | null,
      taxCertificate: null as File | null,
    },
    email: "",
    operatingHours: {
      friday: { close: "23:00", isOpen: true, open: "09:00" },
      monday: { close: "22:00", isOpen: true, open: "09:00" },
      saturday: { close: "23:00", isOpen: true, open: "08:00" },
      sunday: { close: "22:00", isOpen: true, open: "08:00" },
      thursday: { close: "22:00", isOpen: true, open: "09:00" },
      tuesday: { close: "22:00", isOpen: true, open: "09:00" },
      wednesday: { close: "22:00", isOpen: true, open: "09:00" },
    },
    phone: "",
    restaurantName: "",
    website: "",
  });
  // Handle cover photo upload
  const handleCoverPhotoChange = async (
    e: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const file = e.target.files?.[0];
    if (file) {
      setFormData((prev) => ({
        ...prev,
        coverPhoto: file,
        coverPhotoUrl: URL.createObjectURL(file),
      }));
      // Optionally, upload immediately or on form submit
      try {
        // Replace with your actual upload API/service
        await documentsService.uploadDocument("cover_photo", file);
        toast.success("Cover photo uploaded successfully.");
      } catch (err) {
        toast.error("Failed to upload cover photo.");
      }
    }
  };

  // Load business info on mount
  useEffect(() => {
    if (!showDocuments) {
      businessInfoService.getBusinessInfo().then((info) => {
        console.log("BusinessInfoService response:", info);
        // Normalize operating_hours to object with day keys if array or empty
        const apiHours = (info.operating_hours || {}) as any;
        const defaultHours = {
          friday: { close: "", isOpen: false, open: "" },
          monday: { close: "", isOpen: false, open: "" },
          saturday: { close: "", isOpen: false, open: "" },
          sunday: { close: "", isOpen: false, open: "" },
          thursday: { close: "", isOpen: false, open: "" },
          tuesday: { close: "", isOpen: false, open: "" },
          wednesday: { close: "", isOpen: false, open: "" },
        };
        const mappedHours = Object.keys(defaultHours).reduce(
          (acc, day) => {
            const dayData = apiHours[day] || {};
            (acc as any)[day] = {
              close: dayData.close ?? "",
              isOpen: Boolean(dayData.isOpen),
              open: dayData.open ?? "",
            };
            return acc;
          },
          {} as typeof defaultHours,
        );
        setFormData((prev) => ({
          ...prev,
          address: info.address || "",
          cuisineType: info.cuisine_type ? info.cuisine_type.toLowerCase() : "",
          description: info.description || "",
          email: info.email || "",
          operatingHours: mappedHours,
          phone: info.phone || "",
          restaurantName: info.company_name || "",
          website: info.website || "",
        }));
      });
    }
  }, [showDocuments]);

  const [uploadedDocuments, setUploadedDocuments] = useState<{
    [key: string]: boolean;
  }>({});
  const [loadingDocuments, setLoadingDocuments] = useState(false);
  const [replacingDoc, setReplacingDoc] = useState<null | string>(null);
  const [deletingDoc, setDeletingDoc] = useState<null | string>(null);
  // const [confirmOpen, setConfirmOpen] = useState(false); // Removed duplicate

  // Fetch uploaded documents on mount if in documents mode
  useEffect(() => {
    if (showDocuments) {
      setLoadingDocuments(true);
      documentsService.getUserDocuments().then((response) => {
        const uploadedState: { [key: string]: boolean } = {};
        if (response.documents && Array.isArray(response.documents)) {
          response.documents.forEach((doc) => {
            let key = doc.document_type || doc.type;
            if (key === "business_license") key = "businessLicense";
            if (key === "health_permit") key = "healthPermit";
            if (key === "menu_sample") key = "menuSample";
            if (key === "tax_document") key = "taxCertificate";
            if (key === "cover_photo") key = "coverPhoto";
            uploadedState[key] = true;
          });
        }
        setUploadedDocuments(uploadedState);
        setLoadingDocuments(false);
      });
    }
  }, [showDocuments]);
  const handleInputChange = (field: string, value: string) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleHoursChange = (
    day: keyof typeof formData.operatingHours,
    field: "close" | "isOpen" | "open",
    value: boolean | string,
  ) => {
    setFormData((prev) => ({
      ...prev,
      operatingHours: {
        ...prev.operatingHours,
        [day]: {
          ...prev.operatingHours[day],
          [field]: value,
        },
      },
    }));
  };

  const handleFileUpload = async (documentType: string, file: File) => {
    if (showDocuments) {
      // Map all frontend keys to backend types
      const docTypeMap: { [key: string]: string } = {
        businessLicense: "business_license",
        coverPhoto: "cover_photo",
        healthPermit: "health_permit",
        menuSample: "menu_sample",
        taxCertificate: "tax_document",
      };
      const backendType = docTypeMap[documentType] || documentType;
      try {
        await documentsService.uploadDocument(backendType, file);
        setUploadedDocuments((prev) => ({ ...prev, [documentType]: true }));
        toast.success("Document uploaded successfully.");
        if (typeof onDocumentUploaded === "function") onDocumentUploaded();
      } catch (e: any) {
        const backendMsg = e?.response?.data?.message;
        if (
          backendMsg &&
          backendMsg.includes("already exists") &&
          backendMsg.includes("replace_existing=true")
        ) {
          toast.error(
            "A document of this type already exists. To replace it, please use the 'Replace' option.",
          );
        } else if (e?.response?.data?.errors) {
          toast.error(
            "Validation error: " + JSON.stringify(e.response.data.errors),
          );
        } else if (backendMsg) {
          toast.error(backendMsg);
        } else {
          toast.error("Failed to upload document. Please try again.");
        }
      }
    } else {
      setFormData((prev) => ({
        ...prev,
        documents: {
          ...prev.documents,
          [documentType]: file,
        },
      }));
      setUploadedDocuments((prev) => ({ ...prev, [documentType]: true }));
    }
  };
  const handleDelete = async () => {
    setDeleting(true);
    try {
      // Simulate delete API call
      await new Promise((resolve) => setTimeout(resolve, 1000));
      toast.success("Application deleted successfully.");
      // setConfirmOpen(false); // Removed, not needed
    } catch (err) {
      toast.error("Failed to delete application.");
    } finally {
      setDeleting(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    // Prepare payload for backend
    const { cuisineType, operatingHours, ...rest } = formData;
    const payload = {
      ...rest,
      cuisine_type: cuisineType,
      operating_hours: operatingHours,
    };
    try {
      await businessInfoService.updateBusinessInfo(payload);
      toast.success("Business info updated successfully!");
      if (onSubmit) onSubmit(formData);
    } catch (err: any) {
      const backendMsg = err?.response?.data?.message;
      toast.error(
        backendMsg || err.message || "Failed to update business info.",
      );
    }
  };

  const requiredDocuments = [
    {
      description: "Valid business registration and operating license",
      key: "businessLicense",
      label: "Business License",
      required: true,
    },
    {
      description: "Food service health department permit",
      key: "healthPermit",
      label: "Health Permit",
      required: true,
    },
    {
      description: "Tax registration and compliance certificate",
      key: "taxCertificate",
      label: "Tax Certificate",
      required: true,
    },
    {
      description: "Sample menu with prices (PDF format preferred)",
      key: "menuSample",
      label: "Menu Sample",
      required: false,
    },
    {
      description:
        "Cover photo for your restaurant's landing page (JPG/PNG, recommended size: 1200x600px)",
      key: "coverPhoto",
      label: "Cover Photo",
      required: false,
    },
  ];

  const dayNames = {
    friday: "Friday",
    monday: "Monday",
    saturday: "Saturday",
    sunday: "Sunday",
    thursday: "Thursday",
    tuesday: "Tuesday",
    wednesday: "Wednesday",
  };

  return (
    <div className="rounded-lg bg-white p-4 shadow-lg dark:bg-gray-800 sm:p-6 lg:p-8">
      <HotToast />
      <div className="mb-4 sm:mb-6">
        <h2 className="mb-1.5 text-xl font-bold text-gray-900 dark:text-gray-100 sm:mb-2 sm:text-2xl">
          Restaurant Partnership Application
        </h2>
        <p className="text-sm text-gray-600 dark:text-gray-400 sm:text-base">
          Join our platform and start serving customers in your area.
        </p>
      </div>

      <form className="space-y-6 sm:space-y-8" onSubmit={handleSubmit}>
        {!showDocuments && (
          <>
            {/* Basic Information */}
            <div>
              <h3 className="mb-3 text-base font-semibold text-gray-900 dark:text-gray-100 sm:mb-4 sm:text-lg">
                Restaurant Information
              </h3>
              <div className="grid grid-cols-1 gap-3 sm:gap-4 md:grid-cols-2">
                <div>
                  <label className="mb-1.5 block text-xs font-medium text-gray-700 dark:text-gray-300 sm:mb-2 sm:text-sm">
                    Restaurant Name *
                  </label>
                  <input
                    className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none dark:border-gray-600 dark:bg-gray-700 dark:text-white sm:text-base"
                    onChange={(e) =>
                      handleInputChange("restaurantName", e.target.value)
                    }
                    placeholder="Your Restaurant Name"
                    required
                    type="text"
                    value={formData.restaurantName || ""}
                  />
                </div>
                <div>
                  <label className="mb-1.5 block text-xs font-medium text-gray-700 dark:text-gray-300 sm:mb-2 sm:text-sm">
                    Cuisine Type *
                  </label>
                  <select
                    className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none dark:border-gray-600 dark:bg-gray-700 dark:text-white sm:text-base"
                    onChange={(e) =>
                      handleInputChange("cuisineType", e.target.value)
                    }
                    required
                    value={formData.cuisineType || ""}
                  >
                    <option value="">Select Cuisine Type</option>
                    <option value="italian">Italian</option>
                    <option value="chinese">Chinese</option>
                    <option value="indian">Indian</option>
                    <option value="american">American</option>
                    <option value="mexican">Mexican</option>
                    <option value="thai">Thai</option>
                    <option value="japanese">Japanese</option>
                    <option value="mediterranean">Mediterranean</option>
                    <option value="other">Other</option>
                  </select>
                </div>
                <div className="md:col-span-2">
                  <label className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">
                    Address *
                  </label>
                  <textarea
                    className="w-full rounded-lg border border-gray-300 px-3 py-2 focus:border-blue-500 focus:outline-none dark:border-gray-600 dark:bg-gray-700 dark:text-white"
                    onChange={(e) =>
                      handleInputChange("address", e.target.value)
                    }
                    placeholder="Full restaurant address"
                    required
                    rows={3}
                    value={formData.address || ""}
                  />
                </div>
                <div>
                  <label className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">
                    Phone Number *
                  </label>
                  <input
                    className="w-full rounded-lg border border-gray-300 px-3 py-2 focus:border-blue-500 focus:outline-none dark:border-gray-600 dark:bg-gray-700 dark:text-white"
                    onChange={(e) => handleInputChange("phone", e.target.value)}
                    placeholder="+1 (555) 123-4567"
                    required
                    type="tel"
                    value={formData.phone || ""}
                  />
                </div>
                <div>
                  <label className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">
                    Email Address *
                  </label>
                  <input
                    className="w-full rounded-lg border border-gray-300 px-3 py-2 focus:border-blue-500 focus:outline-none dark:border-gray-600 dark:bg-gray-700 dark:text-white"
                    onChange={(e) => handleInputChange("email", e.target.value)}
                    placeholder="restaurant@email.com"
                    readOnly
                    required
                    type="email"
                    value={formData.email || ""}
                  />
                </div>
                <div className="md:col-span-2">
                  <label className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">
                    Website
                  </label>
                  <input
                    className="w-full rounded-lg border border-gray-300 px-3 py-2 focus:border-blue-500 focus:outline-none dark:border-gray-600 dark:bg-gray-700 dark:text-white"
                    onChange={(e) =>
                      handleInputChange("website", e.target.value)
                    }
                    placeholder="www.yourwebsite.com"
                    type="text"
                    value={formData.website || ""}
                  />
                </div>
                <div className="md:col-span-2">
                  <label className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">
                    Description
                  </label>
                  <textarea
                    className="w-full rounded-lg border border-gray-300 px-3 py-2 focus:border-blue-500 focus:outline-none dark:border-gray-600 dark:bg-gray-700 dark:text-white"
                    onChange={(e) =>
                      handleInputChange("description", e.target.value)
                    }
                    placeholder="Tell us about your restaurant, specialties, and what makes you unique..."
                    rows={4}
                    value={formData.description || ""}
                  />
                </div>
              </div>
            </div>
            {/* Operating Hours */}
            <div>
              <h3 className="mb-4 text-lg font-semibold text-gray-900 dark:text-gray-100">
                Operating Hours
              </h3>
              <div className="space-y-3">
                {Object.entries(formData.operatingHours).map(([day, hours]) => (
                  <div
                    className="flex flex-col gap-2.5 rounded-lg bg-gray-50 p-3 dark:bg-gray-700 sm:flex-row sm:items-center sm:justify-between sm:gap-3"
                    key={day}
                  >
                    <div className="flex items-center gap-3">
                      <input
                        checked={hours.isOpen}
                        className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                        onChange={(e) =>
                          handleHoursChange(
                            day as keyof typeof formData.operatingHours,
                            "isOpen",
                            e.target.checked,
                          )
                        }
                        type="checkbox"
                      />
                      <span className="min-w-[5rem] font-medium capitalize text-gray-900 dark:text-gray-100">
                        {dayNames[day as keyof typeof dayNames]}
                      </span>
                    </div>
                    {hours.isOpen ? (
                      <div className="flex flex-1 items-center gap-1.5 pl-7 sm:flex-none sm:gap-2 sm:pl-0">
                        <input
                          className="min-w-0 flex-1 rounded border border-gray-300 px-1.5 py-1.5 text-sm dark:border-gray-600 dark:bg-gray-600 dark:text-white sm:w-auto sm:flex-none sm:px-2"
                          onChange={(e) =>
                            handleHoursChange(
                              day as keyof typeof formData.operatingHours,
                              "open",
                              e.target.value,
                            )
                          }
                          type="time"
                          value={hours.open || ""}
                        />
                        <span className="flex-shrink-0 text-xs text-gray-500 sm:text-sm">
                          to
                        </span>
                        <input
                          className="min-w-0 flex-1 rounded border border-gray-300 px-1.5 py-1.5 text-sm dark:border-gray-600 dark:bg-gray-600 dark:text-white sm:w-auto sm:flex-none sm:px-2"
                          onChange={(e) =>
                            handleHoursChange(
                              day as keyof typeof formData.operatingHours,
                              "close",
                              e.target.value,
                            )
                          }
                          type="time"
                          value={hours.close || ""}
                        />
                      </div>
                    ) : (
                      <span className="pl-7 text-gray-500 dark:text-gray-400 sm:pl-0">
                        Closed
                      </span>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </>
        )}

        {/* Required Documents */}
        {showDocuments && (
          <div>
            <h3 className="mb-4 text-lg font-semibold text-gray-900 dark:text-gray-100">
              Required Documents
            </h3>
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              {requiredDocuments.map((doc) => {
                const [docConfirmOpen, setDocConfirmOpen] = useState(false);
                const [docDeleting, setDocDeleting] = useState(false);
                const handleDeleteDocument = async () => {
                  setDocDeleting(true);
                  try {
                    // Simulate delete API call
                    await new Promise((resolve) => setTimeout(resolve, 1000));
                    setUploadedDocuments((prev) => ({
                      ...prev,
                      [doc.key]: false,
                    }));
                    toast.success(`${doc.label} deleted successfully.`);
                    setDocConfirmOpen(false);
                  } catch (err) {
                    toast.error(`Failed to delete ${doc.label}.`);
                  } finally {
                    setDocDeleting(false);
                  }
                };
                return (
                  <div
                    className="rounded-lg border border-gray-200 p-4 dark:border-gray-700"
                    key={doc.key}
                  >
                    <div className="mb-2 flex items-center justify-between">
                      <h4 className="font-medium text-gray-900 dark:text-gray-100">
                        {doc.label}
                        {doc.required && (
                          <span className="ml-1 text-red-500">*</span>
                        )}
                      </h4>
                      {uploadedDocuments[doc.key] && (
                        <>
                          <Check className="h-5 w-5 text-green-500" />
                        </>
                      )}
                    </div>
                    <p className="mb-3 text-sm text-gray-600 dark:text-gray-400">
                      {doc.description}
                    </p>
                    {uploadedDocuments[doc.key] ? (
                      <>
                        <div className="mt-2 flex items-center justify-center rounded-lg border border-green-400 bg-green-900/20 p-4 text-green-300 dark:bg-green-900/30">
                          <svg
                            className="mr-2 h-8 w-8 text-green-400"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                            viewBox="0 0 24 24"
                          >
                            <path
                              d="M9 12l2 2l4 -4"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            />
                            <circle cx="12" cy="12" r="10" />
                          </svg>
                          <span className="text-base font-semibold text-green-200">
                            Document uploaded successfully
                          </span>
                        </div>
                        <div className="mt-2 flex justify-center gap-4">
                          <label className="cursor-pointer text-blue-600 hover:underline">
                            <input
                              accept=".pdf,.jpg,.jpeg,.png,.doc,.docx"
                              className="hidden"
                              disabled={loadingDocuments}
                              onChange={async (e) => {
                                const file = e.target.files?.[0];
                                if (file) {
                                  setLoadingDocuments(true);
                                  // Find backend type
                                  let backendType = doc.key;
                                  if (backendType === "businessLicense")
                                    backendType = "business_license";
                                  if (backendType === "healthPermit")
                                    backendType = "health_permit";
                                  if (backendType === "menuSample")
                                    backendType = "menu_sample";
                                  if (backendType === "taxCertificate")
                                    backendType = "tax_document";
                                  try {
                                    await documentsService.replaceDocument(
                                      backendType,
                                      file,
                                      doc.description,
                                    );
                                    setUploadedDocuments((prev) => ({
                                      ...prev,
                                      [doc.key]: true,
                                    }));
                                    toast.success(
                                      "Document replaced successfully.",
                                    );
                                  } catch {
                                    toast.error("Failed to replace document.");
                                  }
                                  setLoadingDocuments(false);
                                }
                              }}
                              type="file"
                            />
                            Replace
                          </label>
                          <button
                            className="text-red-600 hover:underline"
                            disabled={loadingDocuments}
                            onClick={async () => {
                              setDeletingDoc(doc.key);
                              setLoadingDocuments(true);
                              // Find backend type
                              let backendType = doc.key;
                              if (backendType === "businessLicense")
                                backendType = "business_license";
                              if (backendType === "healthPermit")
                                backendType = "health_permit";
                              if (backendType === "menuSample")
                                backendType = "menu_sample";
                              if (backendType === "taxCertificate")
                                backendType = "tax_document";
                              try {
                                // Find document id from backend if needed
                                const response =
                                  await documentsService.getUserDocuments();
                                const found = response.documents.find(
                                  (d) =>
                                    (d.document_type || d.type) === backendType,
                                );
                                if (found) {
                                  await documentsService.deleteDocument(
                                    found.id,
                                  );
                                  setUploadedDocuments((prev) => ({
                                    ...prev,
                                    [doc.key]: false,
                                  }));
                                  toast.success("Document deleted");
                                }
                              } catch {
                                toast.error("Failed to delete document.");
                              }
                              setLoadingDocuments(false);
                            }}
                            type="button"
                          >
                            Delete
                          </button>
                        </div>
                      </>
                    ) : (
                      <div className="text-center">
                        <Upload className="mx-auto mb-2 h-8 w-8 text-gray-400" />
                        <p className="text-sm text-gray-600 dark:text-gray-400">
                          Click to upload or drag and drop
                        </p>
                        <input
                          accept=".pdf,.jpg,.jpeg,.png,.doc,.docx"
                          className="hidden"
                          disabled={loadingDocuments}
                          id={`file-upload-${doc.key}`}
                          onChange={(e) => {
                            const file = e.target.files?.[0];
                            if (file) handleFileUpload(doc.key, file);
                          }}
                          type="file"
                        />
                        <label
                          className="cursor-pointer text-blue-600 hover:underline"
                          htmlFor={`file-upload-${doc.key}`}
                        >
                          Upload
                        </label>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}
        {/* Submit Button (hidden in business documents tab) */}
        {!showDocuments && (
          <div className="flex justify-end">
            <button
              className="flex items-center gap-2 rounded-lg bg-blue-600 px-6 py-3 text-white hover:bg-blue-700 disabled:opacity-50"
              disabled={isSubmitting}
              type="submit"
            >
              {isSubmitting ? (
                <>
                  <div className="h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent"></div>
                  Saving...
                </>
              ) : (
                "Save Changes"
              )}
            </button>
          </div>
        )}
      </form>

      {/* Information Box */}
      <div className="mt-6 rounded-lg bg-blue-50 p-4 dark:bg-blue-900/20">
        <div className="flex items-start gap-3">
          <AlertCircle className="mt-0.5 h-5 w-5 flex-shrink-0 text-blue-600 dark:text-blue-400" />
          <div>
            <h4 className="font-medium text-blue-900 dark:text-blue-400">
              Application Review Process
            </h4>
            <p className="mt-1 text-sm text-blue-800 dark:text-blue-300">
              After submission, our team will review your application within 3-5
              business days. You'll receive an email with the status update and
              next steps.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RestaurantApplicationCard;
